/**
 * @fileoverview Person data source and type definitions.
 */

import { PersonSchema, type Person } from '@schema/index';
import { createBaseDataSource } from './base-data-source';
import { createAccountTrackingFields, createArchivedAtField, createBooleanField, createCreatedAtField, createDateField, createDeletedAtField, createIdField, createJsonField, createSelectField, createStringField, createUpdatedAtField, FIELD_TYPE_WIDTHS, FIELD_WIDTHS } from './field-factories';
import { getCache } from './cache-registry';
import { createOptionLoader } from './option-loader';

export type { Person };

const organisationLoader = createOptionLoader('organisation');
const areaLoader = createOptionLoader('area');
const managerLoader = createOptionLoader('person', 'first_name', 'id', '(No Manager)');

export const personDataSource = createBaseDataSource<Person>({
  endpoint: 'person',
  searchField: 'first_name',
  schema: PersonSchema,
  fields: [
    createIdField(),
    createStringField('first_name', 'First Name', FIELD_WIDTHS.SMALL),
    createStringField('last_name', 'Last Name', FIELD_WIDTHS.SMALL),
    createDateField('dob', 'Date of Birth', 100),
    createStringField('email', 'Email', FIELD_TYPE_WIDTHS.EMAIL),
    createStringField('phone', 'Phone', FIELD_TYPE_WIDTHS.PHONE),
    createStringField('bio', 'Bio', FIELD_WIDTHS.SMALL),
    createStringField('aspects', 'Aspects', FIELD_WIDTHS.SMALL),
    createStringField('avatar', 'Avatar', FIELD_WIDTHS.SMALL),
    createStringField('job_title', 'Job Title', FIELD_WIDTHS.SMALL),
    createStringField('department', 'Department', FIELD_WIDTHS.SMALL),
    createStringField('facebook', 'Facebook', FIELD_WIDTHS.SMALL),
    createStringField('linkedin', 'Linkedin', FIELD_WIDTHS.SMALL),
    createStringField('github', 'Github', FIELD_WIDTHS.SMALL),
    createStringField('instagram', 'Instagram', FIELD_WIDTHS.SMALL),
    createStringField('website', 'Website', FIELD_WIDTHS.SMALL),
    createSelectField('manager_id', 'Manager', managerLoader.getOptions(), FIELD_WIDTHS.SMALL),
    createSelectField('area_id', 'Area', areaLoader.getOptions(), FIELD_WIDTHS.SMALL),
    createSelectField('organisation_id', 'Organisation', organisationLoader.getOptions(), FIELD_WIDTHS.SMALL),
    createStringField('address_line1', 'Address Line 1', FIELD_WIDTHS.SMALL),
    createStringField('address_line2', 'Address Line 2', FIELD_WIDTHS.SMALL),
    createStringField('city_town', 'City/Town', FIELD_WIDTHS.SMALL),
    createStringField('state_county', 'State/County', FIELD_WIDTHS.SMALL),
    createStringField('postal_code', 'Postal Code', FIELD_WIDTHS.SMALL),
    createStringField('country_code', 'Country Code', FIELD_WIDTHS.SMALL),
    createBooleanField('is_pinned', 'Is Pinned', FIELD_WIDTHS.SMALL),
    createBooleanField('is_vip', 'Is VIP', FIELD_WIDTHS.SMALL),
    createJsonField('metadata', 'Metadata', FIELD_WIDTHS.SMALL),
    ...createAccountTrackingFields({ includeArchived: true }),
    createArchivedAtField(),
    createCreatedAtField(),
    createUpdatedAtField(),
    createDeletedAtField(),
  ],
});

export const personCache = getCache('person');
